/**
 * Gössl Theme - Cart Drawer
 * Handles slide-out cart functionality with AJAX updates
 */

(function() {
  'use strict';

  const SELECTORS = {
    drawer: '[data-cart-drawer]',
    closeBtn: '[data-cart-drawer-close]',
    body: '[data-cart-drawer-body]',
    items: '[data-cart-items]',
    empty: '[data-cart-empty]',
    footer: '[data-cart-footer]',
    subtotal: '[data-cart-subtotal]',
    quantityInput: '[data-quantity-input]',
    quantityDecrease: '[data-quantity-decrease]',
    quantityIncrease: '[data-quantity-increase]',
    removeItem: '[data-remove-item]',
    cartToggle: '[data-cart-toggle]'
  };

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector(SELECTORS.drawer);
      if (!this.drawer) return;

      this.isOpen = false;
      this.isUpdating = false;
      this.focusTrap = null;

      this.init();
    }

    init() {
      this.bindEvents();
      this.bindCartToggle();
    }

    bindEvents() {
      // Close buttons
      this.drawer.querySelectorAll(SELECTORS.closeBtn).forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Quantity buttons
      this.drawer.addEventListener('click', (e) => {
        const decrease = e.target.closest(SELECTORS.quantityDecrease);
        const increase = e.target.closest(SELECTORS.quantityIncrease);
        const remove = e.target.closest(SELECTORS.removeItem);

        if (decrease) {
          const key = decrease.dataset.quantityDecrease;
          this.updateQuantity(key, -1);
        }

        if (increase) {
          const key = increase.dataset.quantityIncrease;
          this.updateQuantity(key, 1);
        }

        if (remove) {
          const key = remove.dataset.removeItem;
          this.removeItem(key);
        }
      });

      // Quantity input change — validate + clamp to min/max
      // Also handle Lieblingsgeschäft select — persist to cart.attributes
      // immediately so checkout (incl. drawer→Zur Kasse) carries the value.
      this.drawer.addEventListener('change', (e) => {
        if (e.target.matches(SELECTORS.quantityInput)) {
          const key = e.target.dataset.quantityInput;
          let quantity = parseInt(e.target.value, 10);
          // Clamp to valid range, default invalid → 1
          if (isNaN(quantity) || quantity < 0) quantity = 1;
          if (quantity > 99) quantity = 99;
          // Reflect clamped value in UI immediately
          e.target.value = quantity;
          this.setQuantity(key, quantity);
        } else if (e.target.id === 'drawer-lieblingsgeschaeft') {
          this.persistCartAttribute('lieblingsgeschaeft', e.target.value);
        }
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Listen for cart updates from other components
      document.addEventListener('cart:updated', () => {
        this.refresh();
      });

      // Listen for add-to-cart: refresh contents and open drawer
      document.addEventListener('cart:open', () => {
        this.refresh().then(() => this.open());
      });
    }

    bindCartToggle() {
      document.querySelectorAll(SELECTORS.cartToggle).forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle();
        });
      });
    }

    open() {
      if (this.isOpen) return;

      this.isOpen = true;
      this.drawer.hidden = false;

      // Force reflow
      this.drawer.offsetHeight;

      this.drawer.classList.add('is-open');
      document.body.classList.add('cart-drawer-open');

      // Trap focus
      if (window.Goessl?.trapFocus) {
        this.focusTrap = window.Goessl.trapFocus(this.drawer);
      }

      // Focus close button
      this.drawer.querySelector(SELECTORS.closeBtn)?.focus();
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('cart-drawer-open');

      // Remove focus trap
      if (this.focusTrap) {
        this.focusTrap();
        this.focusTrap = null;
      }

      // Return focus to toggle
      document.querySelector(SELECTORS.cartToggle)?.focus();

      // Hide after transition
      setTimeout(() => {
        if (!this.isOpen) {
          this.drawer.hidden = true;
        }
      }, 300);
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    async updateQuantity(key, delta) {
      if (this.isUpdating) return;

      const input = this.drawer.querySelector(`[data-quantity-input="${key}"]`);
      if (!input) return;

      const currentQuantity = parseInt(input.value, 10);
      const newQuantity = Math.max(0, currentQuantity + delta);

      await this.setQuantity(key, newQuantity);
    }

    async setQuantity(key, quantity) {
      if (this.isUpdating) return;
      this.isUpdating = true;

      this.setLoading(true);
      this.setItemUpdating(key, true);
      this.clearError();

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });

        if (!response.ok) {
          // Try to extract Shopify error message
          let serverMsg = null;
          try {
            const errorBody = await response.json();
            serverMsg = errorBody.description || errorBody.message;
          } catch (_) {}
          const err = new Error(serverMsg || `HTTP ${response.status}`);
          err.type = response.status >= 500 ? 'server' : 'validation';
          throw err;
        }

        const cart = await response.json();
        this.updateUI(cart);
        this.updateCartCount(cart.item_count);

      } catch (error) {
        console.error('Cart update error:', error);

        // Distinguish network vs server vs validation errors
        const t = window.GoesslCartTranslations || {};
        let message;
        if (error.name === 'TypeError' || error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
          message = t.error_network || 'Keine Verbindung. Bitte prüfen Sie Ihre Internetverbindung.';
        } else if (error.type === 'server') {
          message = t.error_server || 'Der Warenkorb konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.';
        } else {
          message = error.message && !error.message.startsWith('HTTP')
            ? error.message
            : (t.error_generic || 'Der Artikel konnte nicht aktualisiert werden.');
        }

        this.showError(message, () => this.setQuantity(key, quantity));

        // Reset input to previous value
        const input = this.drawer.querySelector(`[data-quantity-input="${key}"]`);
        if (input) {
          this.refresh();
        }
      } finally {
        this.isUpdating = false;
        this.setLoading(false);
        this.setItemUpdating(key, false);
      }
    }

    setItemUpdating(key, updating) {
      const item = this.drawer.querySelector(`[data-cart-item="${CSS.escape(key)}"]`);
      if (item) {
        item.classList.toggle('is-updating', updating);
        item.querySelectorAll('button, input').forEach(el => {
          el.disabled = updating;
        });
      }
    }

    showError(message, retryFn) {
      // Remove any existing error
      this.clearError();

      const errorEl = document.createElement('div');
      errorEl.className = 'cart-drawer__error';
      errorEl.setAttribute('role', 'alert');
      errorEl.setAttribute('data-cart-error', '');

      const text = document.createElement('span');
      text.className = 'cart-drawer__error-text';
      text.textContent = message;
      errorEl.appendChild(text);

      const t = window.GoesslCartTranslations || {};
      if (retryFn) {
        const retryBtn = document.createElement('button');
        retryBtn.type = 'button';
        retryBtn.className = 'cart-drawer__error-retry';
        retryBtn.textContent = t.retry || 'Erneut versuchen';
        retryBtn.addEventListener('click', () => {
          this.clearError();
          retryFn();
        });
        errorEl.appendChild(retryBtn);
      }

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'cart-drawer__error-close';
      closeBtn.setAttribute('aria-label', t.close || 'Schließen');
      closeBtn.textContent = '×';
      closeBtn.addEventListener('click', () => this.clearError());
      errorEl.appendChild(closeBtn);

      // Insert at top of drawer body
      const body = this.drawer.querySelector(SELECTORS.body);
      if (body) {
        body.insertBefore(errorEl, body.firstChild);
      }

      // Auto-dismiss after 8 seconds
      this.errorTimeout = setTimeout(() => this.clearError(), 8000);
    }

    clearError() {
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
        this.errorTimeout = null;
      }
      const existing = this.drawer.querySelector('[data-cart-error]');
      if (existing) existing.remove();
    }

    async removeItem(key) {
      await this.setQuantity(key, 0);
    }

    /**
     * Persist a cart-level attribute (e.g. lieblingsgeschaeft) so it carries
     * through to checkout regardless of whether the customer visits the cart
     * page first. Same pattern as cart-page.js.
     */
    async persistCartAttribute(key, value) {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { [key]: value } })
        });
        if (!response.ok) throw new Error('Failed to persist cart attribute');
      } catch (error) {
        console.error('Error persisting cart attribute', key, error);
      }
    }

    async refresh() {
      try {
        const response = await fetch('/cart.js', {
          headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) throw new Error('Fetch failed');

        const cart = await response.json();
        this.updateUI(cart);

      } catch (error) {
        console.error('Cart refresh error:', error);
      }
    }

    updateUI(cart) {
      const body = this.drawer.querySelector(SELECTORS.body);
      const footer = this.drawer.querySelector(SELECTORS.footer);
      const subtotal = this.drawer.querySelector(SELECTORS.subtotal);

      if (cart.item_count === 0) {
        // Show empty state — built via DOM methods (safer than innerHTML)
        const allProductsUrl = (window.GoesslRoutes && window.GoesslRoutes.all_products) || '/collections/all';
        body.textContent = '';
        const emptyDiv = document.createElement('div');
        emptyDiv.className = 'cart-drawer__empty';
        emptyDiv.setAttribute('data-cart-empty', '');
        const p = document.createElement('p');
        p.textContent = this.getTranslation('cart.general.empty');
        emptyDiv.appendChild(p);
        const link = document.createElement('a');
        link.href = allProductsUrl;
        link.className = 'btn btn--primary';
        link.textContent = this.getTranslation('cart.general.continue_shopping');
        emptyDiv.appendChild(link);
        body.appendChild(emptyDiv);
        if (footer) footer.setAttribute('hidden', '');
      } else {
        // Ensure items container exists (may have been replaced by empty state)
        if (!this.drawer.querySelector(SELECTORS.items)) {
          const ul = document.createElement('ul');
          ul.className = 'cart-drawer__items';
          ul.setAttribute('data-cart-items', '');
          body.textContent = '';
          body.appendChild(ul);
        }
        this.renderItems(cart);
        if (footer) footer.removeAttribute('hidden');
        if (subtotal) subtotal.textContent = this.formatMoney(cart.total_price);
      }
    }

    renderItems(cart) {
      const itemsContainer = this.drawer.querySelector(SELECTORS.items);
      if (!itemsContainer) return;

      itemsContainer.innerHTML = cart.items.map(item => `
        <li class="cart-drawer__item" data-cart-item="${item.key}">
          <div class="cart-drawer__item-image">
            ${item.image ? `
              <img
                src="${this.getSizedImage(item.image, '150x')}"
                alt="${this.escapeHtml(item.image.alt || item.title)}"
                width="75"
                height="100"
                loading="lazy"
              >
            ` : ''}
          </div>

          <div class="cart-drawer__item-details">
            <a href="${item.url}" class="cart-drawer__item-title">
              ${this.escapeHtml(item.product_title)}
            </a>

            ${item.variant_title && item.variant_title !== 'Default Title' ? `
              <p class="cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>
            ` : ''}

            <p class="cart-drawer__item-price">
              ${this.formatMoney(item.final_line_price)}
            </p>

            ${item.properties && item.properties['_Lieferzeit'] ? `
              <p class="cart-drawer__item-delivery-note">Lieferzeit ${this.escapeHtml(item.properties['_Lieferzeit'])}</p>
            ` : ''}

            <div class="cart-drawer__item-quantity">
              <button
                type="button"
                class="cart-drawer__quantity-btn"
                data-quantity-decrease="${item.key}"
                aria-label="Decrease quantity"
              >
                <span aria-hidden="true">−</span>
              </button>
              <input
                type="number"
                class="cart-drawer__quantity-input"
                value="${item.quantity}"
                min="0"
                data-quantity-input="${item.key}"
                aria-label="Quantity"
              >
              <button
                type="button"
                class="cart-drawer__quantity-btn"
                data-quantity-increase="${item.key}"
                aria-label="Increase quantity"
              >
                <span aria-hidden="true">+</span>
              </button>
            </div>
          </div>

          <button
            type="button"
            class="cart-drawer__item-remove"
            data-remove-item="${item.key}"
            aria-label="Remove ${this.escapeHtml(item.product_title)}"
          >
            <svg class="icon icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </li>
      `).join('');
    }

    updateCartCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = count;
      });

      // Update live region (German: same word for singular/plural but text is still read by SR)
      const liveRegion = document.getElementById('cart-live-region');
      if (liveRegion) {
        const t = window.GoesslCartTranslations || {};
        const template = count === 1 ? (t.items_in_cart_one || '1 Artikel im Warenkorb')
                                     : (t.items_in_cart_other || '{count} Artikel im Warenkorb');
        liveRegion.textContent = template.replace('{count}', count);
      }
    }

    setLoading(loading) {
      this.drawer.classList.toggle('is-loading', loading);
    }

    formatMoney(cents) {
      if (window.Goessl?.formatMoney) {
        return window.Goessl.formatMoney(cents);
      }
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }

    getSizedImage(url, size) {
      return url.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, '.')
        .replace(/\.jpg|\.png|\.gif|\.jpeg/g, (match) => `_${size}${match}`);
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    getTranslation(key) {
      // Translations are injected from Liquid via window.GoesslCartTranslations.
      // Falls back to sensible German defaults if not injected (e.g., on old cached pages).
      const t = window.GoesslCartTranslations || {};
      const fallbacks = {
        'cart.general.empty': 'Ihr Warenkorb ist leer',
        'cart.general.continue_shopping': 'Einkauf fortsetzen'
      };
      const shortKey = key.replace(/^cart\.general\./, '');
      return t[shortKey] || fallbacks[key] || key;
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CartDrawer());
  } else {
    new CartDrawer();
  }
})();
